import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { Delete, WarningAmber, WrongLocation } from '@mui/icons-material';
import { Button, Stack, Typography } from '@mui/material';
import * as turf from '@turf/turf';

import { useAppSelector } from 'state/hooks';
import {
  InternalBoundaryPolygon,
  UserProjectSubscription,
} from 'state/userAdminSlices';
import { selectMapStyle } from 'state/workflowSlice';

import { SecondaryText } from 'features/common/SecondaryText';
import { MapContext } from 'features/map/MapContext';
import { initMap, mapHasLayer } from 'features/map/utils/mapCommon';
import { fitBoundsToBbox, isGeometryOnMap } from 'features/map/utils/mapUtils';
import { initZoom, safeCenter } from 'features/map/utils/map_constants';
import {
  isGeomGood,
  useMapSubscriptionBoundary,
} from 'features/user_admin/adminMapCommon';
import {
  formatArea,
  formatAreaMi2,
  formattedAreaFromBoundary,
  mi2Tokm2,
  subscriptionUsesMetric,
} from 'features/user_admin/common';

import styles from 'features/user_admin/NewUserProjectRequestPage.module.css';

const kDrawPolygonFillId = 'gl-draw-polygon-fill.cold';
const kDrawPolygonFillHotId = 'gl-draw-polygon-fill.hot';
const projectMinAreaKm2 = 0.25;

export function ProjectBoundaryEditorMap({
  boundary,
  setBoundary,
  subscription,
  project_max_area_mi2,
  boundaryError,
  setBoundaryError,
}: {
  boundary?: InternalBoundaryPolygon;
  setBoundary: (InternalBoundaryPolygon?) => void;
  subscription: UserProjectSubscription;
  project_max_area_mi2: number;
  boundaryError: string;
  setBoundaryError: (string?) => void;
}) {
  const refMapContainer = useRef(null); // DOM node reference
  const { refMap, mapReady, setMapReady, refStyleReady } =
    useContext(MapContext);
  const refDraw = useRef<MapboxDraw>(undefined);

  const mapStyle = useAppSelector(selectMapStyle);
  const [hasPointSelection, setHasPointSelection] = useState(false);
  const subscriptionBoundary = useMemo(() => {
    return (
      subscription?.template?.region_boundary &&
      turf.feature(subscription?.template?.region_boundary)
    );
  }, [subscription]);
  useMapSubscriptionBoundary(subscriptionBoundary, boundary);

  function handleMapLoadCallback() {
    setMapReady(true);
    refStyleReady.current = true;
  }

  const getCurrentFeature = () => {
    return refDraw.current.getAll().features[0];
  };

  const setCursorForMode = (mode: string) => {
    let cursor = '';
    if (mode === 'draw_polygon') {
      cursor = 'crosshair';
    }
    if (refMap.current) {
      refMap.current.getCanvas().style.cursor = cursor;
    }
  };

  useEffect(() => {
    const unmount = () => {
      // console.log('Unmount map');
      setMapReady(false);
      refStyleReady.current = false;
      // Wipe old map
      if (refMap.current) {
        refMap.current.remove();
        refMap.current = undefined;
        refDraw.current = undefined;
      }
    };
    // Wipe old map
    if (refMap.current) {
      refMap.current.remove();
      refMap.current = undefined;
      setMapReady(false);
      refStyleReady.current = false;
      refDraw.current = undefined;
    }

    // Init new map
    const center = { ...safeCenter };
    if (subscriptionBoundary) {
      const sub_center = turf.centroid(subscriptionBoundary);
      // eslint-disable-next-line prefer-destructuring
      center.lat = sub_center.geometry.coordinates[1];
      // eslint-disable-next-line prefer-destructuring
      center.lng = sub_center.geometry.coordinates[0];
    }

    initMap(refMap, refMapContainer, center, initZoom, mapStyle, false, {
      handleMapLoadCallback,
    });
    const draw = new MapboxDraw({
      displayControlsDefault: false,
      // Select which mapbox-gl-draw control buttons to add to the map.
      controls: {},
      modes: {
        ...MapboxDraw.modes,
        direct_select: { ...MapboxDraw.modes.direct_select, dragFeature() {} },
      },
      // Set mapbox-gl-draw to draw by default.
      // The user does not have to click the polygon control button first.
      defaultMode: 'draw_polygon',
    });
    refMap.current.addControl(draw);

    function updateArea(e) {
      const geom = getCurrentFeature()?.geometry;
      if (isGeomGood(geom)) {
        setBoundary(geom);
      } else {
        setBoundary(undefined);
      }
    }

    refMap.current.on('draw.create', updateArea);
    refMap.current.on('draw.delete', updateArea);
    refMap.current.on('draw.update', updateArea);
    refMap.current.on('draw.selectionchange', () => {
      setHasPointSelection(
        refDraw.current?.getSelectedPoints().features.length > 0,
      );
    });
    setCursorForMode('draw_polygon');
    refMap.current.on('draw.modechange', (e) => {
      let new_mode = e.mode;
      if (e.mode === 'simple_select') {
        const id = draw.getAll().features[0]?.id;
        if (id) {
          new_mode = 'direct_select';
          draw.changeMode(new_mode, { featureId: id });
        } else {
          new_mode = 'draw_polygon';
          draw.changeMode(new_mode);
        }
        setCursorForMode(new_mode);
      }
    });

    if (boundary) {
      fitBoundsToBbox(refMap.current, boundary, 0.1);
    } else if (subscriptionBoundary) {
      fitBoundsToBbox(refMap.current, subscriptionBoundary.geometry, 0.1);
    }

    refDraw.current = draw;

    return unmount;
  }, [mapStyle, subscription]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mapReady) {
      const currently_drawn = getCurrentFeature()?.geometry;
      if (
        isGeomGood(boundary) &&
        (!isGeomGood(currently_drawn) ||
          !turf.booleanEqual(currently_drawn, boundary))
      ) {
        // console.log('Redraw boundary', boundary);
        refDraw.current.deleteAll();
        refDraw.current.add(boundary);
        refDraw.current.changeMode('direct_select', {
          featureId: getCurrentFeature().id,
        });
        setCursorForMode('direct_select');
        if (!isGeometryOnMap(boundary, refMap.current)) {
          fitBoundsToBbox(refMap.current, boundary, 0.1);
        }
      } else if (!boundary) {
        refDraw.current.deleteAll();
        refDraw.current.changeMode('draw_polygon');
        setCursorForMode('draw_polygon');
      }
    }
  }, [boundary, mapReady]);

  const setBoundaryStyles = (good: boolean) => {
    const color = good ? 'yellow' : 'red';
    if (mapReady) {
      if (mapHasLayer(refMap.current, kDrawPolygonFillId)) {
        refMap.current.setPaintProperty(
          kDrawPolygonFillId,
          'fill-color',
          color,
        );
      }
      if (mapHasLayer(refMap.current, kDrawPolygonFillHotId)) {
        refMap.current.setPaintProperty(
          kDrawPolygonFillHotId,
          'fill-color',
          color,
        );
      }
    }
  };

  useEffect(() => {
    if (boundary && mapReady && refStyleReady.current) {
      const sadnesses = [];
      try {
        if (!isGeomGood(boundary)) {
          sadnesses.push(
            'Boundary geometry is invalid (no points or malformed)',
          );
        } else {
          const area = turf.area(boundary);
          const area_km2 = area / 1000000;
          const convex_area = turf.area(turf.convex(boundary));
          if (area_km2 > mi2Tokm2(project_max_area_mi2)) {
            sadnesses.push(
              `Selected area is larger than the limit of ${formatAreaMi2(project_max_area_mi2, subscriptionUsesMetric(subscription))}`,
            );
          }

          if (area_km2 < projectMinAreaKm2)
            sadnesses.push(
              `Selected area must be larger than ${formatArea(projectMinAreaKm2, subscriptionUsesMetric(subscription))}`,
            );

          if (convex_area * 0.5 > area) {
            sadnesses.push(
              'Selected area should not fold into itself excessively',
            );
          }
          const boundaryTypeOk = boundary.type === 'Polygon';
          if (!boundaryTypeOk)
            sadnesses.push(
              `Invalid boundary type: ${boundary.type}. Project boundary should be of Polygon type.`,
            );
          if (
            boundaryTypeOk &&
            subscriptionBoundary &&
            !turf.booleanContains(subscriptionBoundary, boundary)
          ) {
            sadnesses.push(
              'Selected area goes outside of the subscription boundary',
            );
          }
        }
      } catch (e) {
        sadnesses.push('Something went wrong');
        console.error(e);
      }

      if (sadnesses.length === 0) {
        setBoundaryError(undefined);
        setBoundaryStyles(true);
      } else {
        setBoundaryError(sadnesses.join(';'));
        setBoundaryStyles(false);
      }
    }
    if (!boundary) {
      setBoundaryError(undefined);
      setBoundaryStyles(true);
    }
  }, [boundary, subscriptionBoundary, mapReady]);

  return (
    <div ref={refMapContainer} className={styles.boundary_map_container}>
      <div className={styles.boundary_map_instructions_container}>
        <Stack className={styles.boundary_map_instructions_header}>
          <Typography variant="h2">Draw Project Boundary</Typography>
          {boundary && (
            <Stack direction="row" justifyContent="space-between">
              <SecondaryText>Selected Area</SecondaryText>
              <SecondaryText>
                {formattedAreaFromBoundary(boundary, subscription)}
              </SecondaryText>
            </Stack>
          )}
        </Stack>
        {!boundary && (
          <p className={styles.boundary_map_instructions}>
            Click to add points
            <br />
            Double-click or click the first point to finish
          </p>
        )}
        {boundary && (
          <p className={styles.boundary_map_instructions}>
            Click and drag points to edit the boundary
          </p>
        )}
        {boundaryError &&
          boundaryError.split(';').map((item) => (
            <Stack
              key={item}
              alignItems="center"
              direction="row"
              gap={2}
              justifyContent="left"
              className={styles.boundary_map_error}
            >
              <WarningAmber />
              <Typography>{item}</Typography>
            </Stack>
          ))}
        <Stack direction="row" className={styles.boundary_map_controls}>
          <Button
            startIcon={<Delete />}
            onClick={(ev) => {
              setBoundary(undefined);
            }}
            disabled={!boundary}
          >
            Clear Polygon
          </Button>
          <Button
            startIcon={<WrongLocation />}
            onClick={(ev) => {
              if (refDraw.current && mapReady) {
                refDraw.current.trash();
              }
            }}
            disabled={!hasPointSelection}
          >
            Delete point
          </Button>
        </Stack>
      </div>
    </div>
  );
}
